import React, { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal, X } from "lucide-react";

// CS 20.3 — shared sort + range-filter bar used on:
//  - Distribution Catalog admin
//  - Distributor public link
//  - Retailer public link
//
// Operates on values AT THE CURRENT PURITY (the caller passes already-priced items).
// State is purely local to this component's parent — selections + colours are NOT touched.

export const DEFAULT_FILTERS = {
  sort: "default",
  mrpMin: "",
  mrpMax: "",
  wtMin: "",
  wtMax: "",
};

// CS 20.3 — Admin variant of the filter state. The admin Distribution Catalog
// view shows raw catalog designs (base-purity net_wt + diamond_wt) without a
// live purity/rate context, so MRP is intentionally omitted.
export const DEFAULT_ADMIN_FILTERS = {
  sort: "default",
  wtMin: "",
  wtMax: "",
  dwtMin: "",
  dwtMax: "",
};

const SORT_OPTIONS = [
  { value: "default", label: "Default order" },
  { value: "mrp_desc", label: "Customer MRP: High → Low" },
  { value: "mrp_asc", label: "Customer MRP: Low → High" },
  { value: "wt_desc", label: "Gold Weight: High → Low" },
  { value: "wt_asc", label: "Gold Weight: Low → High" },
];

const ADMIN_SORT_OPTIONS = [
  { value: "default", label: "Default order" },
  { value: "wt_desc", label: "Gold Wt (base): High → Low" },
  { value: "wt_asc", label: "Gold Wt (base): Low → High" },
  { value: "dwt_desc", label: "Diamond Wt: High → Low" },
  { value: "dwt_asc", label: "Diamond Wt: Low → High" },
];

// Strip commas/spaces and parse as a float. Returns NaN for empty or invalid input.
const parseRange = (s) => {
  if (s == null || s === "") return NaN;
  const cleaned = String(s).replace(/[,\s]/g, "");
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : NaN;
};

// Coerce a DB-stored value to a finite number; returns NaN if not parseable.
const numOrNaN = (v) => {
  if (v == null || v === "") return NaN;
  if (typeof v === "number") return Number.isFinite(v) ? v : NaN;
  const n = parseFloat(String(v));
  return Number.isFinite(n) ? n : NaN;
};

// Apply filters + sort to an array of items. Item must have:
//   item.pricing.customer_mrp  (for MRP filters/sort)
//   item.pricing.gold_weight   (for Gold-Wt filters/sort — converted weight at selected purity)
export function applyFiltersAndSort(items, filters) {
  if (!items) return items;
  const { sort, mrpMin, mrpMax, wtMin, wtMax } = filters;
  const lo = (k) => parseRange(k);

  const mrpLo = lo(mrpMin), mrpHi = lo(mrpMax);
  const wtLo = lo(wtMin), wtHi = lo(wtMax);

  let out = items.filter((p) => {
    const pr = p.pricing || {};
    const mrp = pr.customer_mrp;
    const wt = pr.gold_weight;
    if (!Number.isNaN(mrpLo) && (mrp ?? -Infinity) < mrpLo) return false;
    if (!Number.isNaN(mrpHi) && (mrp ?? Infinity) > mrpHi) return false;
    if (!Number.isNaN(wtLo) && (wt ?? -Infinity) < wtLo) return false;
    if (!Number.isNaN(wtHi) && (wt ?? Infinity) > wtHi) return false;
    return true;
  });

  if (sort === "mrp_desc") out = [...out].sort((a, b) => (b.pricing?.customer_mrp ?? 0) - (a.pricing?.customer_mrp ?? 0));
  else if (sort === "mrp_asc") out = [...out].sort((a, b) => (a.pricing?.customer_mrp ?? 0) - (b.pricing?.customer_mrp ?? 0));
  else if (sort === "wt_desc") out = [...out].sort((a, b) => (b.pricing?.gold_weight ?? 0) - (a.pricing?.gold_weight ?? 0));
  else if (sort === "wt_asc") out = [...out].sort((a, b) => (a.pricing?.gold_weight ?? 0) - (b.pricing?.gold_weight ?? 0));
  return out;
}

// Bounds helper for placeholder hints
export function priceBounds(items) {
  if (!items || items.length === 0) return { mrpMin: null, mrpMax: null, wtMin: null, wtMax: null };
  let mrpMin = Infinity, mrpMax = -Infinity, wtMin = Infinity, wtMax = -Infinity;
  for (const p of items) {
    const m = p.pricing?.customer_mrp;
    const w = p.pricing?.gold_weight;
    if (typeof m === "number") { if (m < mrpMin) mrpMin = m; if (m > mrpMax) mrpMax = m; }
    if (typeof w === "number") { if (w < wtMin) wtMin = w; if (w > wtMax) wtMax = w; }
  }
  return {
    mrpMin: Number.isFinite(mrpMin) ? Math.floor(mrpMin) : null,
    mrpMax: Number.isFinite(mrpMax) ? Math.ceil(mrpMax) : null,
    wtMin: Number.isFinite(wtMin) ? wtMin.toFixed(2) : null,
    wtMax: Number.isFinite(wtMax) ? wtMax.toFixed(2) : null,
  };
}

// CS 20.4 — admin filter helpers. Extracted to keep applyAdminFiltersAndSort
// below complexity 10 (linter cap) and make each branch independently testable.
function _adminMatches(p, wtLo, wtHi, dwLo, dwHi, wtBound, dwBound) {
  const w = numOrNaN(p?.net_wt);
  const d = numOrNaN(p?.diamond_wt);
  if (wtBound && Number.isNaN(w)) return false;
  if (!Number.isNaN(wtLo) && w < wtLo) return false;
  if (!Number.isNaN(wtHi) && w > wtHi) return false;
  if (dwBound && Number.isNaN(d)) return false;
  if (!Number.isNaN(dwLo) && d < dwLo) return false;
  if (!Number.isNaN(dwHi) && d > dwHi) return false;
  return true;
}

const _ADMIN_SORTERS = {
  wt_desc:  (a, b) => (numOrNaN(b?.net_wt)     || 0) - (numOrNaN(a?.net_wt)     || 0),
  wt_asc:   (a, b) => (numOrNaN(a?.net_wt)     || 0) - (numOrNaN(b?.net_wt)     || 0),
  dwt_desc: (a, b) => (numOrNaN(b?.diamond_wt) || 0) - (numOrNaN(a?.diamond_wt) || 0),
  dwt_asc:  (a, b) => (numOrNaN(a?.diamond_wt) || 0) - (numOrNaN(b?.diamond_wt) || 0),
};

// Admin Catalog filter logic — operates on raw catalog_products rows.
//   item.net_wt      → gold weight at the design's base purity (e.g. 18kt)
//   item.diamond_wt  → diamond weight (carats)
// No MRP is computed at this layer; the admin page is purity/rate agnostic.
//
// Null-safety: missing/null/string net_wt or diamond_wt never throw. They are
// passed through when no bound is set, and excluded when any bound is set
// (you can't satisfy a numeric constraint with a missing value).
export function applyAdminFiltersAndSort(items, filters) {
  if (!items) return items;
  const f = filters || DEFAULT_ADMIN_FILTERS;
  const wtLo = parseRange(f.wtMin), wtHi = parseRange(f.wtMax);
  const dwLo = parseRange(f.dwtMin), dwHi = parseRange(f.dwtMax);
  const wtBound = !Number.isNaN(wtLo) || !Number.isNaN(wtHi);
  const dwBound = !Number.isNaN(dwLo) || !Number.isNaN(dwHi);

  let out;
  try {
    out = items.filter((p) => _adminMatches(p, wtLo, wtHi, dwLo, dwHi, wtBound, dwBound));
  } catch {
    return items; // never crash the UI — bail back to full list
  }

  const sorter = _ADMIN_SORTERS[f.sort];
  return sorter ? [...out].sort(sorter) : out;
}

export function adminBounds(items) {
  if (!items || items.length === 0) return { wtMin: null, wtMax: null, dwtMin: null, dwtMax: null };
  let wtMin = Infinity, wtMax = -Infinity, dwMin = Infinity, dwMax = -Infinity;
  for (const p of items) {
    const w = numOrNaN(p?.net_wt), d = numOrNaN(p?.diamond_wt);
    if (!Number.isNaN(w)) { if (w < wtMin) wtMin = w; if (w > wtMax) wtMax = w; }
    if (!Number.isNaN(d)) { if (d < dwMin) dwMin = d; if (d > dwMax) dwMax = d; }
  }
  return {
    wtMin: Number.isFinite(wtMin) ? wtMin.toFixed(2) : null,
    wtMax: Number.isFinite(wtMax) ? wtMax.toFixed(2) : null,
    dwtMin: Number.isFinite(dwMin) ? dwMin.toFixed(2) : null,
    dwtMax: Number.isFinite(dwMax) ? dwMax.toFixed(2) : null,
  };
}

export default function CatalogFilterBar({ filters, onChange, bounds, theme = "light", variant = "public" }) {
  const [open, setOpen] = useState(false);
  const isAdmin = variant === "admin";
  const active = isAdmin
    ? (filters.sort !== "default" ||
       filters.wtMin !== "" || filters.wtMax !== "" ||
       filters.dwtMin !== "" || filters.dwtMax !== "")
    : (filters.sort !== "default" ||
       filters.mrpMin !== "" || filters.mrpMax !== "" ||
       filters.wtMin !== "" || filters.wtMax !== "");

  const set = (k, v) => onChange({ ...filters, [k]: v });
  const reset = () => onChange(isAdmin ? DEFAULT_ADMIN_FILTERS : DEFAULT_FILTERS);

  const isDark = theme === "dark";
  const baseBtn = isDark
    ? "bg-white text-[#2a2520] hover:bg-white/90 border border-white"
    : "bg-white text-[#0D1B2A] hover:bg-[#FAF8F3] border border-[#0D1B2A]/15";
  const activeBtn = "bg-[#b8893b] text-white border-[#b8893b] hover:bg-[#a37730]";

  const sortOptions = isAdmin ? ADMIN_SORT_OPTIONS : SORT_OPTIONS;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          type="button"
          className={`h-9 rounded-full px-4 text-xs tracking-wide ${active ? activeBtn : baseBtn}`}
          data-testid="filter-bar-toggle"
        >
          <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" />
          Filters / Sort{active ? " · ON" : ""}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[340px] p-4 space-y-3 text-[#0D1B2A] bg-white"
        data-testid="filter-panel"
      >
        <div className="flex justify-between items-center pb-1 border-b border-[#0D1B2A]/10">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#b8893b] font-semibold">Filter &amp; sort</p>
          <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Sort by</label>
          <Select value={filters.sort} onValueChange={(v) => set("sort", v)}>
            <SelectTrigger className="h-9" data-testid="filter-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              {sortOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {!isAdmin && (
          <RangeRow
            label="Customer MRP (₹)"
            placeholderLo={bounds?.mrpMin}
            placeholderHi={bounds?.mrpMax}
            lo={filters.mrpMin} hi={filters.mrpMax}
            onLo={(v) => set("mrpMin", v)} onHi={(v) => set("mrpMax", v)}
            testid="filter-mrp"
          />
        )}
        <RangeRow
          label={isAdmin ? "Gold Wt (base, g)" : "Gold Weight (g)"}
          placeholderLo={bounds?.wtMin}
          placeholderHi={bounds?.wtMax}
          lo={filters.wtMin} hi={filters.wtMax}
          onLo={(v) => set("wtMin", v)} onHi={(v) => set("wtMax", v)}
          step="0.01"
          testid="filter-wt"
        />
        {isAdmin && (
          <RangeRow
            label="Diamond Weight (ct)"
            placeholderLo={bounds?.dwtMin}
            placeholderHi={bounds?.dwtMax}
            lo={filters.dwtMin} hi={filters.dwtMax}
            onLo={(v) => set("dwtMin", v)} onHi={(v) => set("dwtMax", v)}
            step="0.01"
            testid="filter-dwt"
          />
        )}

        <div className="flex justify-between pt-2 border-t border-[#0D1B2A]/10">
          <Button size="sm" variant="ghost" onClick={reset} disabled={!active} className="h-8 text-xs text-slate-600" data-testid="filter-reset">
            Clear filters
          </Button>
          <Button size="sm" onClick={() => setOpen(false)} className="h-8 text-xs bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid="filter-apply">
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function RangeRow({ label, placeholderLo, placeholderHi, lo, hi, onLo, onHi, step = "1", testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">{label}</label>
      <div className="flex items-center gap-2">
        <Input
          type="text"
          inputMode="decimal"
          value={lo}
          onChange={(e) => onLo(e.target.value)}
          placeholder={placeholderLo != null ? `from ${placeholderLo}` : "from"}
          className="h-9 text-xs"
          data-testid={`${testid}-min`}
        />
        <span className="text-xs text-slate-400">to</span>
        <Input
          type="text"
          inputMode="decimal"
          value={hi}
          onChange={(e) => onHi(e.target.value)}
          placeholder={placeholderHi != null ? `to ${placeholderHi}` : "to"}
          className="h-9 text-xs"
          data-testid={`${testid}-max`}
        />
      </div>
    </div>
  );
}
